import { FC } from "react";

import {
  VnAlertDialog,
  VnAlertDialogAction,
  VnAlertDialogCancel,
  VnAlertDialogContent,
  VnAlertDialogDescription,
  VnAlertDialogFooter,
  VnAlertDialogHeader,
  VnAlertDialogTitle,
} from "@/components/VnAlertDialog";
import {
  useCurrentHouseholdMemberQuery,
  useListHouseholdMembersQuery,
} from "@/data/queries/householdMembersQueries";
import { ProgramConflictType } from "@/domain/applications";
import { formatMemberList, getProgramTypeLabel } from "@/utils/brandLabel";
import { formatHouseholdMemberName } from "@/utils/householdMember";

const getDescriptionI18nKey = (conflict: ProgramConflictType | null) => {
  if (conflict?.shouldRecommendMedicaid) {
    return "native:applicationForm.programConflictDialog.recommendMedicaidDescription";
  }

  if (conflict && conflict.conflictingPrograms.length > 1) {
    return "native:applicationForm.programConflictDialog.multipleDescription";
  }

  return "native:applicationForm.programConflictDialog.description";
};

type ApplicationProgramConflictDialogPropsType = {
  conflict: ProgramConflictType | null;
  onKeep: () => void;
  onSwitch: () => void;
};

export const ApplicationProgramConflictDialog: FC<
  ApplicationProgramConflictDialogPropsType
> = ({ conflict, onKeep, onSwitch }) => {
  const { data: householdMembers } = useListHouseholdMembersQuery();
  const { data: currentHouseholdMember } = useCurrentHouseholdMemberQuery();

  const currentPrograms = formatMemberList({
    names: conflict?.conflictingPrograms.map(getProgramTypeLabel) ?? [],
  });

  const newProgram = conflict ? getProgramTypeLabel(conflict.newProgram) : "";

  const memberNames = formatMemberList({
    names: (conflict?.conflictingMemberIds ?? []).flatMap(memberId => {
      const member = householdMembers?.find(({ id }) => id === memberId);
      return member ? [formatHouseholdMemberName(member, "firstNameOnly")] : [];
    }),
  });

  const isCurrentPatientOnly =
    conflict?.conflictingMemberIds.length === 1 &&
    conflict.conflictingMemberIds[0] === currentHouseholdMember?.id;

  const hasMultipleMembers = (conflict?.conflictingMemberIds.length ?? 0) > 1;

  const descriptionI18nOptions = {
    context: isCurrentPatientOnly
      ? "self"
      : hasMultipleMembers
        ? "members"
        : undefined,
    currentPrograms,
    newProgram,
    memberNames,
  };

  return (
    <VnAlertDialog
      open={conflict !== null}
      onOpenChange={isOpen => {
        if (!isOpen) onKeep();
      }}>
      <VnAlertDialogContent>
        <VnAlertDialogHeader>
          <VnAlertDialogTitle
            i18nKey={
              conflict?.shouldRecommendMedicaid
                ? "native:applicationForm.programConflictDialog.recommendMedicaidTitle"
                : "native:applicationForm.programConflictDialog.title"
            }
          />
          <VnAlertDialogDescription
            i18nKey={getDescriptionI18nKey(conflict)}
            i18nOptions={descriptionI18nOptions}
          />
        </VnAlertDialogHeader>
        <VnAlertDialogFooter>
          <VnAlertDialogAction
            variant="outline"
            i18nKey="native:applicationForm.programConflictDialog.switchButton"
            i18nOptions={{ newProgram }}
            onPress={onSwitch}
          />
          <VnAlertDialogCancel
            variant="default"
            i18nKey="native:applicationForm.programConflictDialog.keepButton"
            i18nOptions={{ currentPrograms }}
          />
        </VnAlertDialogFooter>
      </VnAlertDialogContent>
    </VnAlertDialog>
  );
};
